import React, { useMemo, useState } from "react";

// SuperLiquidHome.jsx
// Mobile-first, responsive, dark-themed catalog UI for a chemical / cleaning products e-commerce
// - Categories as top tabs with emojis
// - Subcategory tabs shown after selecting a category
// - Grid layout of dummy product cards
// - Filters: Company, Location, Product Name (search), Category, Subcategory
// - Tailwind CSS classes (mobile-first). Default export a React component.

export default function SuperLiquidHome() {
  // Dummy categories with emojis and subcategories
  const categories = [
    {
      id: "household",
      label: "Household 💧",
      subs: ["Multi-Surface", "Floor", "Toilet", "Glass"],
    },
    {
      id: "sanitizers",
      label: "Sanitizers 🧴",
      subs: ["Hand Gel", "Surface Spray", "Wipes", "Concentrate"],
    },
    {
      id: "laundry",
      label: "Laundry 🧺",
      subs: ["Powder", "Liquid", "Softener", "Stain Remover"],
    },
    {
      id: "raw",
      label: "Raw Materials 🧪",
      subs: ["Surfactants", "Solvents", "Fragrances", "Preservatives"],
    },
    {
      id: "aircare",
      label: "Air Care 🌬️",
      subs: ["Room Spray", "Diffuser", "Car Freshener"],
    },
  ];

  // Dummy product data
  const PRODUCTS = [
    {
      id: 1,
      name: "Citrus Multi-Surface Cleaner 500ml",
      company: "GreenChem Labs",
      location: "Mumbai",
      category: "household",
      subcategory: "Multi-Surface",
      size: "500 ml",
      active: "Anionic Surfactant",
      price: 149,
      stock: 120,
      scent: "Citrus",
    },
    {
      id: 2,
      name: "70% Ethanol Hand Gel 100ml",
      company: "PureTech Sanitizers",
      location: "Delhi",
      category: "sanitizers",
      subcategory: "Hand Gel",
      size: "100 ml",
      active: "Ethanol 70%",
      price: 59,
      stock: 450,
      scent: "Unscented",
    },
    {
      id: 3,
      name: "Industrial SLES 70% - 25kg",
      company: "ChemSource",
      location: "Vadodara",
      category: "raw",
      subcategory: "Surfactants",
      size: "25 kg",
      active: "SLES 70%",
      price: 4200,
      stock: 20,
      scent: "None",
    },
    {
      id: 4,
      name: "Lavender Room Spray 250ml",
      company: "AromaWorks",
      location: "Bengaluru",
      category: "aircare",
      subcategory: "Room Spray",
      size: "250 ml",
      active: "Fragrance Blend",
      price: 199,
      stock: 80,
      scent: "Lavender",
    },
    {
      id: 5,
      name: "Fabric Softener Liquid 2L",
      company: "BrightWash",
      location: "Kolkata",
      category: "laundry",
      subcategory: "Softener",
      size: "2 L",
      active: "Cationic Polymer",
      price: 249,
      stock: 60,
      scent: "Floral",
    },
    {
      id: 6,
      name: "Surface Disinfectant Spray - 1L",
      company: "PureTech Sanitizers",
      location: "Delhi",
      category: "sanitizers",
      subcategory: "Surface Spray",
      size: "1 L",
      active: "QAC 0.5%",
      price: 399,
      stock: 35,
      scent: "Lemon",
    },
    {
      id: 7,
      name: "Glass Cleaner 500ml",
      company: "SparkleChem",
      location: "Chennai",
      category: "household",
      subcategory: "Glass",
      size: "500 ml",
      active: "Isopropyl Alcohol 5%",
      price: 129,
      stock: 200,
      scent: "Odorless",
    },
    {
      id: 8,
      name: "Degreaser Concentrate 5L",
      company: "ChemSource",
      location: "Vadodara",
      category: "raw",
      subcategory: "Solvents",
      size: "5 L",
      active: "Glycol Ether Blend",
      price: 1999,
      stock: 15,
      scent: "Solvent",
    },
  ];

  // UI state
  const [activeCategory, setActiveCategory] = useState(categories[0].id);
  const [activeSub, setActiveSub] = useState(categories[0].subs[0]);

  const [searchText, setSearchText] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  // Derive lists for filters
  const companies = useMemo(() => {
    const s = new Set(PRODUCTS.map((p) => p.company));
    return Array.from(s);
  }, []);

  const locations = useMemo(() => {
    const s = new Set(PRODUCTS.map((p) => p.location));
    return Array.from(s);
  }, []);

  // Filtered products
  const filtered = useMemo(() => {
    return PRODUCTS.filter((p) => {
      if (p.category !== activeCategory) return false;
      if (activeSub && p.subcategory !== activeSub) return false;
      if (companyFilter && p.company !== companyFilter) return false;
      if (locationFilter && p.location !== locationFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        if (
          !(
            p.name.toLowerCase().includes(q) ||
            p.company.toLowerCase().includes(q) ||
            p.active.toLowerCase().includes(q)
          )
        )
          return false;
      }
      return true;
    });
  }, [activeCategory, activeSub, companyFilter, locationFilter, searchText]);

  // Helpers
  const onSelectCategory = (catId) => {
    setActiveCategory(catId);
    const cat = categories.find((c) => c.id === catId);
    setActiveSub(cat?.subs?.[0] ?? "");
  };

  return (
    <div className="min-h-screen bg-black text-gray-100 p-4 sm:p-6 md:p-10">
      <header className="mb-2 bg-red-900 text-center">
        <h1 className="text-xl sm:text-2xl font-semibold">SuperLiquid</h1>
        <p className="text-xs lg:text-lg text-white">Ultimate ecosystem and marketplace for chemical & cleaning products</p>
      </header>

      {/* Category tabs (mobile-first: horizontal scroll) */}
      <nav className="mb-2 ">
        <div className="grid grid-cols-2 md:grid-cols-6 lg:grid-cols-6 xl:grid-cols-8 gap-0 w-full border-b place-items-center">
            {categories.map((cat) => (
                <button
                key={cat.id}
                onClick={() => onSelectCategory(cat.id)}
                className={`w-full text-center whitespace-nowrap border text-sm font-medium px-2 py-2 
                    ${activeCategory === cat.id
                    ? "bg-indigo-600 border-indigo-600 text-white"
                    : "bg-gray-800 border-gray-700 text-gray-200/90"}
                `}
                >
                {cat.label}
                </button>
            ))}
        </div>
      </nav>

      {/* Subcategory tabs */}
      <div className="mb-2">
        <div className="grid grid-cols-2 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-6 xl:grid-cols-8 gap-0 w-full place-items-center">
            {categories
            .find((c) => c.id === activeCategory)
            ?.subs?.map((s) => (
                <button
                key={s}
                onClick={() => setActiveSub(s)}
                className={`w-full text-center border text-xs font-medium px-2 py-2 
                    ${activeSub === s
                    ? "bg-emerald-600 border-emerald-600 text-white"
                    : "bg-gray-800 border-gray-700 text-gray-200/90"}
                `}
                >
                {s}
                </button>
            ))}
        </div>
        </div>


      {/* Filters & Search */}
      <section className="mb-2">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex-1">
            <label className="sr-only">Search products</label>
            <div className="relative">
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by product name, company or active ingredient"
                className="w-full rounded-lg bg-gray-800 border border-gray-700 placeholder-gray-400 text-gray-100 px-3 py-2 text-sm"
              />
              <div className="absolute right-2 top-2 text-gray-400 text-xs">⌕</div>
            </div>
          </div>

          <div className="flex justify-center gap-2">
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="rounded-lg h-10 bg-gray-800 border border-gray-700 text-gray-100 px-2 py-2 text-sm"
            >
              <option value="">All Companies</option>
              {companies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="rounded-lg h-10 bg-gray-800 border border-gray-700 text-gray-100 px-2 py-2 text-sm"
            >
              <option value="">All Locations</option>
              {locations.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>

            <button
              onClick={() => {
                setSearchText("");
                setCompanyFilter("");
                setLocationFilter("");
              }}
              className="hidden sm:inline-block rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-sm text-gray-300"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Mobile quick filters row */}
        <div className="mt-3 grid grid-cols-2 py-2 place-items-center gap-2 sm:hidden overflow-x-auto">
          <button
            onClick={() => setCompanyFilter("")}
            className="flex-shrink-0 rounded-full border border-gray-700 px-3 py-1 text-xs text-gray-200"
          >
            All Companies
          </button>
          <button
            onClick={() => setLocationFilter("")}
            className="flex-shrink-0 rounded-full border border-gray-700 px-3 py-1 text-xs text-gray-200"
          >
            All Locations
          </button>
        </div>
      </section>

      {/* Product Grid */}
      <main>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.length === 0 ? (
            <div className="col-span-full rounded-lg bg-gray-800 border border-dashed border-gray-700 p-2 text-center">
              <p className="text-yellow-100">No products match the selected filters.</p>
            </div>
          ) : (
            filtered.map((p) => (
              <article
                key={p.id}
                className="rounded-2xl bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 p-4 flex flex-col"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold leading-snug">{p.name}</h3>
                    <p className="text-xs text-gray-400 mt-1">{p.company} • {p.location}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">₹{p.price}</div>
                    <div className={`text-xs mt-1 ${p.stock > 50 ? 'text-emerald-300' : p.stock>0 ? 'text-yellow-300' : 'text-rose-400'}`}>
                      {p.stock > 50 ? 'In stock' : p.stock>0 ? `Low stock (${p.stock})` : 'Out of stock'}
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-xs text-gray-300">
                  <p><span className="font-medium">Size:</span> {p.size}</p>
                  <p><span className="font-medium">Active:</span> {p.active}</p>
                  <p><span className="font-medium">Scent:</span> {p.scent}</p>
                </div>

                <div className="mt-4 flex items-center justify-between gap-2">
                  <button className="flex-1 rounded-lg border border-gray-700 px-3 py-2 w-40 text-sm text-gray-100">Quick View</button>
                  <button className="rounded-lg bg-indigo-600 px-3 py-2 w-36 text-sm font-medium">Add</button>
                </div>
              </article>
            ))
          )}
        </div>
      </main>

      <div className="mt-8 text-xs text-yellow-200">
        <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
          <div>Showing <span className="text-gray-300 font-medium">{filtered.length}</span> products</div>
          <div className="text-gray-400">Pro-tip: use the search box to find active ingredients, product names or companies.</div>
        </div>
      </div>

      {/* Footer / small legend */}
      <footer className="mt-8 text-xs text-yellow-200">
        <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
          <div className="text-white text-center">(c) Think5Whys Global DeepTech Solutions</div>
        </div>
      </footer>
    </div>
  );
}
